/* eslint-disable no-undef */
import axios, { AxiosError } from 'axios';
import { BackendHttpError, retryBackendRequestForResidency } from './BackendRetry';
import { convertAxiosError, getAxiosHeaders } from './BackendInterface';
import { dataResidencyManager } from './DataResidencyManager';
import { IdpType, UserProfileType, UserType } from '@peekapak/types/DataProtocolTypes';
import { ElementaryUnitName, LessonLanguage } from '@peekapak/types/LessonPlanTypes';
import {
  EventName,
  LoginEvent,
  LogEventRequest,
  AnalyticsEvent,
  StudentEvent,
  EventSource,
  NonStudentEvent,
  InteractionEndTrigger,
  JournalAssignmentOpenEvent,
  JournalAssignmentStoryOpenEvent,
  LessonsStoryOpenEvent,
  LessonsOpenedEvent,
  StorybookOpenEvent,
  StorybookCloseEvent,
  StorybookCompleteEvent,
  InteractionStartEvent,
  InteractionEndEvent,
  InternalControlClickEvent,
  TelemetryEvent,
  LibraryName,
  ClassroomType,
  LibraryViewedEvent,
  LibrarySwitchedEvent,
  LibraryAccessBlockedEvent,
} from '@peekapak/types/AnalyticsTypes';
import { SurveyPromptDecision } from './SurveyPromptDecisionService';
import { getAuthorizationToken, getAnalyticsServicesRoot } from './BackendInterface';
import { debounce } from './debounce';

const logEvent: (event: AnalyticsEvent, withKeepAlive?: boolean) => Promise<void> = async (
  event: AnalyticsEvent,
  withKeepAlive = false,
) => {
  let auth = null;
  try {
    auth = await getAuthorizationToken();
  } catch (error) {
    const expectedMessageFragment = 'User is not logged in or not authenticated';
    if (!(error as Error).message.includes(expectedMessageFragment)) {
      throw error;
    }
  }

  let endpoint = '';
  const request: LogEventRequest = {
    event,
  };

  if (auth) {
    endpoint = `${getAnalyticsServicesRoot()}/analytics/log`;
  } else {
    endpoint = `${getAnalyticsServicesRoot()}/analytics/unauthenticatedlog`;
  }

  if (withKeepAlive) {
    try {
      //utilize fetch to allow for keepalive flag, not currently supported by axios
      await retryBackendRequestForResidency(
        'Analytics.logEvent keepalive',
        async () => {
          const response = await fetch(endpoint, {
            keepalive: true,
            method: 'post',
            body: JSON.stringify(request),
            headers: getAxiosHeaders(auth),
          });

          if (!response.ok) {
            throw new BackendHttpError(`HTTP ${response.status}: ${response.statusText}`, response.status);
          }
        },
        { dataResidency: dataResidencyManager.getCurrentDataResidency() },
      );
    } catch (error) {
      console.error(`Error communicating with server: ${convertAxiosError(error as AxiosError)}`);
      throw convertAxiosError(error as AxiosError);
    }
  } else {
    try {
      await axios({
        method: 'post',
        url: endpoint,
        data: request,
        headers: getAxiosHeaders(auth),
      });
    } catch (error) {
      console.error(`Error communicating with server: ${convertAxiosError(error as AxiosError)}`);
      throw convertAxiosError(error as AxiosError);
    }
  }
};

export const Analytics = {
  interactionStart: debounce(
    async (userProfile: UserProfileType) => {
      const commonProperties = extractCommonUserProperties(userProfile);
      const { userType, userId } = commonProperties;

      const propertiesToLog = (() => {
        if (userType === UserType.STUDENT) {
          const { grade, readingLevel, classroomId } = userProfile;
          const interactionStartProperties: InteractionStartEvent = {
            name: EventName.INTERACTION_START,
            source: EventSource.PEEKAPAK,
            grade,
            readingLevel,
            classroomId,
            ...commonProperties,
            userType,
          };

          const properties = {
            ...interactionStartProperties,
            distinct_id: userId,
          };

          return properties;
        } else {
          const interactionStartProperties: InteractionStartEvent = {
            name: EventName.INTERACTION_START,
            source: EventSource.PEEKAPAK,
            ...commonProperties,
            userType,
          };

          const properties = {
            ...interactionStartProperties,
            distinct_id: userId,
          };

          return properties;
        }
      })();

      await logEvent(propertiesToLog);
    },
    500,
    true,
  ),
  interactionEnd: debounce(
    async (userProfile: UserProfileType, duration: number, trigger: InteractionEndTrigger) => {
      const commonProperties = extractCommonUserProperties(userProfile);
      const { userType, userId } = commonProperties;

      const propertiesToLog = (() => {
        if (userType === UserType.STUDENT) {
          const { grade, readingLevel, classroomId } = userProfile;
          const interactionEndProperties: InteractionEndEvent = {
            name: EventName.INTERACTION_END,
            source: EventSource.PEEKAPAK,
            grade,
            readingLevel,
            classroomId,
            ...commonProperties,
            userType,
            duration,
            trigger,
          };

          const properties = {
            ...interactionEndProperties,
            distinct_id: userId,
          };

          return properties;
        } else {
          const interactionEndProperties: InteractionEndEvent = {
            name: EventName.INTERACTION_END,
            source: EventSource.PEEKAPAK,
            ...commonProperties,
            userType,
            duration,
            trigger,
          };

          const properties = {
            ...interactionEndProperties,
            distinct_id: userId,
          };

          return properties;
        }
      })();

      await logEvent(propertiesToLog, true);
    },
    500,
    true,
  ),
  journalAssignmentOpen: debounce(
    async (assignmentName: string, createdAt: number, userProfile: UserProfileType) => {
      const commonProperties = extractCommonUserProperties(userProfile);
      const { userType, userId } = commonProperties;
      if (userType !== UserType.STUDENT) return;

      const propertiesToLog = (() => {
        const { grade, readingLevel, classroomId } = userProfile;
        const assignmentOpenProperties: JournalAssignmentOpenEvent = {
          name: EventName.JOURNAL_ASSIGNMENT_OPEN,
          source: EventSource.PEEKAPAK,
          grade,
          readingLevel,
          classroomId,
          ...commonProperties,
          userType,
          assignmentName,
          createdAt,
        };

        const properties = {
          ...assignmentOpenProperties,
          distinct_id: userId,
        };

        return properties;
      })();

      await logEvent(propertiesToLog);
    },
    500,
    true,
  ),
  journalAssignmentStoryOpen: debounce(
    async (unit: string, story: string, userProfile: UserProfileType) => {
      const commonProperties = extractCommonUserProperties(userProfile);
      const { userType, userId } = commonProperties;
      if (userType !== UserType.STUDENT) return;

      const propertiesToLog = (() => {
        const { grade, readingLevel, classroomId } = userProfile;
        const assignmentStoryOpenProperties: JournalAssignmentStoryOpenEvent = {
          name: EventName.JOURNAL_STORY_OPEN,
          source: EventSource.PEEKAPAK,
          grade,
          readingLevel,
          classroomId,
          ...commonProperties,
          userType,
          story: buildStoryName(unit, story),
        };

        const properties = {
          ...assignmentStoryOpenProperties,
          distinct_id: userId,
        };

        return properties;
      })();

      await logEvent(propertiesToLog);
    },
    500,
    true,
  ),
  lessonsStoryOpen: debounce(
    async (unit: string, story: string, userProfile: UserProfileType) => {
      const commonProperties = extractCommonUserProperties(userProfile);
      const { userType, userId } = commonProperties;
      if (userType === UserType.STUDENT) return;

      const propertiesToLog = (() => {
        const lessonsStoryOpenProperties: LessonsStoryOpenEvent = {
          name: EventName.LESSONS_STORY_OPEN,
          source: EventSource.PEEKAPAK,
          ...commonProperties,
          userType,
          story: buildStoryName(unit, story),
        };

        const properties = {
          ...lessonsStoryOpenProperties,
          distinct_id: userId,
        };

        return properties;
      })();

      const debouncedLogEvent = debounce(logEvent, 500, true);
      await debouncedLogEvent(propertiesToLog);
    },
    500,
    true,
  ),
  lessonsOpenedEvent: debounce(
    async (unit: string, lesson: string, userProfile: UserProfileType) => {
      const commonProperties = extractCommonUserProperties(userProfile);
      const { userType, userId } = commonProperties;
      if (userType === UserType.STUDENT) return;

      const propertiesToLog = (() => {
        const lessonsOpenedProperties: LessonsOpenedEvent = {
          name: EventName.LESSONS_OPEN,
          source: EventSource.PEEKAPAK,
          ...commonProperties,
          userType,
          unit,
          lesson,
        };

        const properties = {
          ...lessonsOpenedProperties,
          distinct_id: userId,
        };

        return properties;
      })();

      await logEvent(propertiesToLog);
    },
    500,
    true,
  ),
  login: debounce(
    async (userProfile: UserProfileType, method?: IdpType) => {
      const commonProperties = extractCommonUserProperties(userProfile);
      const { userType, userId } = commonProperties;

      const propertiesToLog = (() => {
        if (userType === UserType.STUDENT) {
          const { grade, readingLevel, classroomId } = userProfile;
          const loginProperties: LoginEvent<StudentEvent> = {
            name: EventName.LOGIN,
            source: EventSource.PEEKAPAK,
            grade,
            readingLevel,
            classroomId,
            ...commonProperties,
            userType,
            loginMethod: method || 'unknown',
          };

          const properties = {
            ...loginProperties,
            distinct_id: userId,
          };

          return properties;
        } else {
          const loginProperties: LoginEvent<NonStudentEvent> = {
            name: EventName.LOGIN,
            source: EventSource.PEEKAPAK,
            ...commonProperties,
            userType,
            loginMethod: method || 'unknown',
          };

          const properties = {
            ...loginProperties,
            distinct_id: userId,
          };

          return properties;
        }
      })();

      await logEvent(propertiesToLog);
    },
    500,
    true,
  ),
  pageView: debounce(
    async (url: string, pageName: string | null = null, userProfile: UserProfileType | null = null) => {
      const userProperties = (() => {
        if (userProfile) {
          const commonProperties = extractCommonUserProperties(userProfile);
          const { userType, userId } = commonProperties;

          const propertiesToLog = (() => {
            if (userType === UserType.STUDENT) {
              const { grade, readingLevel, classroomId } = userProfile;
              const loginProperties: LoginEvent<StudentEvent> = {
                name: EventName.LOGIN,
                source: EventSource.PEEKAPAK,
                grade,
                readingLevel,
                classroomId,
                ...commonProperties,
                userType,
                loginMethod: 'unknown',
              };

              const properties = {
                ...loginProperties,
                distinct_id: userId,
              };

              return properties;
            } else {
              const loginProperties: LoginEvent<NonStudentEvent> = {
                name: EventName.LOGIN,
                source: EventSource.PEEKAPAK,
                ...commonProperties,
                userType,
                loginMethod: 'unknown',
              };

              const properties = {
                ...loginProperties,
                distinct_id: userId,
              };

              return properties;
            }
          })();
          return propertiesToLog;
        }

        return {};
      })();

      const propertiesToLog = {
        ...userProperties,
        name: EventName.PAGE_VIEW,
        source: EventSource.PEEKAPAK,
        url,
        pageName: pageName || 'none',
      };

      await logEvent(propertiesToLog);
    },
    500,
    true,
  ),
  storybookCloseEvent: debounce(
    async (
      unit: ElementaryUnitName,
      readingLevel: number,
      language: LessonLanguage,
      userProfile: UserProfileType,
      duration: number,
    ) => {
      const commonProperties = extractCommonUserProperties(userProfile);
      const { userType, userId } = commonProperties;
      if (userType === UserType.STUDENT) return;

      const propertiesToLog = (() => {
        const storybookCloseProperties: StorybookCloseEvent<NonStudentEvent> = {
          name: EventName.JOURNAL_BOOK_CLOSE,
          source: EventSource.PEEKAPAK,
          ...commonProperties,
          unit,
          readingLevel,
          language,
          userType,
          duration,
        };

        const properties = {
          ...storybookCloseProperties,
          distinct_id: userId,
        };

        return properties;
      })();

      await logEvent(propertiesToLog, true);
    },
    500,
    true,
  ),
  storybookCompleteEvent: debounce(
    async (unit: ElementaryUnitName, readingLevel: number, language: LessonLanguage, userProfile: UserProfileType) => {
      const commonProperties = extractCommonUserProperties(userProfile);
      const { userType, userId } = commonProperties;
      if (userType === UserType.STUDENT) return;

      const propertiesToLog = (() => {
        const storybookCompleteProperties: StorybookCompleteEvent<NonStudentEvent> = {
          name: EventName.JOURNAL_BOOK_COMPLETE,
          source: EventSource.PEEKAPAK,
          ...commonProperties,
          unit,
          readingLevel,
          language,
          userType,
        };

        const properties = {
          ...storybookCompleteProperties,
          distinct_id: userId,
        };

        return properties;
      })();

      await logEvent(propertiesToLog);
    },
    500,
    true,
  ),
  storybookOpenEvent: debounce(
    async (unit: ElementaryUnitName, readingLevel: number, language: LessonLanguage, userProfile: UserProfileType) => {
      const commonProperties = extractCommonUserProperties(userProfile);
      const { userType, userId } = commonProperties;
      if (userType === UserType.STUDENT) return;

      const propertiesToLog = (() => {
        const storybookOpenProperties: StorybookOpenEvent<NonStudentEvent> = {
          name: EventName.JOURNAL_BOOK_OPEN,
          source: EventSource.PEEKAPAK,
          ...commonProperties,
          unit,
          readingLevel,
          language,
          userType,
        };

        const properties = {
          ...storybookOpenProperties,
          distinct_id: userId,
        };

        return properties;
      })();

      await logEvent(propertiesToLog);
    },
    500,
    true,
  ),
  libraryViewedEvent: debounce(
    async (
      libraryName: LibraryName,
      classroomType: ClassroomType,
      peekapakAccessStatus: boolean,
      userProfile: UserProfileType,
    ) => {
      const commonProperties = extractCommonUserProperties(userProfile);
      const { userType, userId } = commonProperties;
      if (userType === UserType.STUDENT) return;

      const propertiesToLog = (() => {
        const libraryViewedProperties: LibraryViewedEvent<NonStudentEvent> = {
          name: EventName.LIBRARY_VIEWED,
          source: EventSource.PEEKAPAK,
          ...commonProperties,
          userType,
          libraryName,
          classroomType,
          peekapakAccessStatus,
        };

        const properties = {
          ...libraryViewedProperties,
          distinct_id: userId,
        };

        return properties;
      })();

      await logEvent(propertiesToLog);
    },
    500,
    true,
  ),
  librarySwitchedEvent: debounce(
    async (
      fromLibrary: LibraryName,
      toLibrary: LibraryName,
      classroomType: ClassroomType,
      userProfile: UserProfileType,
    ) => {
      const commonProperties = extractCommonUserProperties(userProfile);
      const { userType, userId } = commonProperties;
      if (userType === UserType.STUDENT) return;

      const propertiesToLog = (() => {
        const librarySwitchedProperties: LibrarySwitchedEvent<NonStudentEvent> = {
          name: EventName.LIBRARY_SWITCHED,
          source: EventSource.PEEKAPAK,
          ...commonProperties,
          userType,
          fromLibrary,
          toLibrary,
          classroomType,
        };

        const properties = {
          ...librarySwitchedProperties,
          distinct_id: userId,
        };

        return properties;
      })();

      await logEvent(propertiesToLog);
    },
    500,
    true,
  ),
  libraryAccessBlockedEvent: debounce(
    async (
      requestedLibrary: LibraryName,
      classroomType: ClassroomType,
      schoolAccessStatus: boolean,
      userProfile: UserProfileType,
    ) => {
      const commonProperties = extractCommonUserProperties(userProfile);
      const { userType, userId } = commonProperties;
      if (userType === UserType.STUDENT) return;

      const propertiesToLog = (() => {
        const libraryAccessBlockedProperties: LibraryAccessBlockedEvent<NonStudentEvent> = {
          name: EventName.LIBRARY_ACCESS_BLOCKED,
          source: EventSource.PEEKAPAK,
          ...commonProperties,
          userType,
          requestedLibrary,
          classroomType,
          schoolAccessStatus,
        };

        const properties = {
          ...libraryAccessBlockedProperties,
          distinct_id: userId,
        };

        return properties;
      })();

      await logEvent(propertiesToLog);
    },
    500,
    true,
  ),
  internalControlClickEvent: debounce(
    async (
      control: string,
      options?: {
        controlType?: 'button' | 'link' | 'icon' | 'menu-item' | 'checkbox' | 'radio' | 'toggle' | 'other';
        scene?: string;
        section?: string;
        action?: string;
        label?: string;
        metadata?: Record<string, unknown>;
        userProfile?: UserProfileType;
      },
    ) => {
      const { controlType, scene, section, action, label, metadata, userProfile } = options || {};
      const userProperties = (() => {
        if (userProfile) {
          const commonProperties = extractCommonUserProperties(userProfile);
          const { userType, userId } = commonProperties;

          const propertiesToLog = (() => {
            if (userType === UserType.STUDENT) {
              const { grade, readingLevel, classroomId } = userProfile;
              const controlClickEventProperties: InternalControlClickEvent = {
                name: EventName.INTERNAL_CONTROL_CLICK,
                source: EventSource.PEEKAPAK,
                grade,
                readingLevel,
                classroomId,
                ...commonProperties,
                userType,
                control,
              };

              const properties = {
                ...controlClickEventProperties,
                distinct_id: userId,
              };

              return properties;
            } else {
              const controlClickEventProperties: InternalControlClickEvent = {
                name: EventName.INTERNAL_CONTROL_CLICK,
                source: EventSource.PEEKAPAK,
                ...commonProperties,
                userType,
                control,
              };

              const properties = {
                ...controlClickEventProperties,
                distinct_id: userId,
              };

              return properties;
            }
          })();
          return propertiesToLog;
        }

        return {
          name: EventName.INTERNAL_CONTROL_CLICK,
          source: EventSource.PEEKAPAK,
          control,
        };
      })();

      const optionalProperties: Partial<InternalControlClickEvent> = {};
      if (controlType) optionalProperties.controlType = controlType;
      if (scene) optionalProperties.scene = scene;
      if (section) optionalProperties.section = section;
      if (action) optionalProperties.action = action;
      if (label) optionalProperties.label = label;
      if (metadata) optionalProperties.metadata = metadata;

      const propertiesToLog = {
        ...userProperties,
        ...optionalProperties,
      };

      await logEvent(propertiesToLog);
    },
    500,
    true,
  ),

  /**
   * Log generic telemetry event
   *
   * CENTRALIZED CHUNK HANDLING - Accepts pre-chunked data array.
   * Callers should NOT do chunking inline - chunking is done in the
   * telemetry-specific service (e.g., SurveyPromptDecisionService).
   *
   * Supports splitting data across multiple fields (telemetryData0-4) to respect
   * Mixpanel's 255-byte string property limit.
   *
   * @param telemetryName - Identifier for telemetry type (e.g., 'surveyPromptDecision')
   * @param telemetryDataChunks - Array of data chunks (assigned to telemetryData0-4)
   * @param userProfile - User profile for common properties
   */
  telemetry: async (
    telemetryName: string,
    telemetryDataChunks: Record<string, any>[],
    userProfile: UserProfileType,
  ) => {
    // Debounce: Check if this same event was recently logged
    // Prevents duplicate events when called from render paths
    const firstChunk = telemetryDataChunks[0] || {};
    if (!shouldLogTelemetry(telemetryName, userProfile.userId, firstChunk)) {
      return; // Skip logging - same event logged recently
    }

    const commonProperties = extractCommonUserProperties(userProfile);

    const event: TelemetryEvent = {
      name: EventName.TELEMETRY,
      source: EventSource.PEEKAPAK,
      ...commonProperties,
      telemetryName,
    };

    // Assign array elements to telemetryData0, telemetryData1, etc.
    // Data is already chunked by the service (e.g., SurveyPromptDecisionService)
    telemetryDataChunks.forEach((chunk, index) => {
      if (index <= 4) {
        const key = `telemetryData${index}` as keyof TelemetryEvent;
        const stringified = JSON.stringify(chunk);

        // Warn if chunk exceeds Mixpanel's 255-byte limit
        // Mixpanel will truncate, but we want to know about it during development
        if (stringified.length > 255) {
          console.warn(`[telemetry] Chunk ${index} exceeds 255 bytes (${stringified.length} bytes):`, {
            telemetryName,
            chunkIndex: index,
            chunkSize: stringified.length,
            chunkPreview: stringified.substring(0, 100) + '...',
          });
        }

        (event as any)[key] = stringified;
      } else {
        console.warn(`[telemetry] Too many data chunks (${telemetryDataChunks.length}), max is 5 (index 0-4)`);
      }
    });

    await logEvent(event);
  },

  /**
   * Log survey prompt decision using telemetry event
   *
   * CENTRALIZED FORWARDING - This method does NOT manipulate data.
   * It receives pre-compiled, pre-chunked data from SurveyPromptDecisionService
   * and forwards it to telemetry as-is.
   *
   * Only logs when backend says user is eligible (filters out noise).
   *
   * @param decision - Survey prompt decision object (includes pre-chunked data)
   * @param userProfile - User profile for common properties
   */
  surveyPromptDecision: async (decision: SurveyPromptDecision, userProfile: UserProfileType) => {
    // Only log if backend says user is eligible
    // This filters out the majority of cases and focuses on decisions we care about
    if (!decision.telemetryData.hasBackendEligibilityFlag) {
      return;
    }

    // Forward pre-chunked data to telemetry (no data manipulation here)
    await Analytics.telemetry('surveyPromptDecision', decision.telemetryDataChunks, userProfile);
  },
};

// Debouncing for telemetry events to prevent duplicate logging
// Tracks recent telemetry events by a unique key
const recentTelemetryEvents = new Map<string, number>();
const TELEMETRY_DEBOUNCE_MS = 5000; // Don't log same event within 5 seconds

/**
 * Generate a unique key for telemetry event deduplication
 * Based on telemetry name, user ID, and critical data fields
 */
function getTelemetryKey(telemetryName: string, userId: string, data: any): string {
  // For survey prompt decision, key includes: name, userId, campaignId, shouldPrompt, decidedBy
  if (telemetryName === 'surveyPromptDecision') {
    return `${telemetryName}:${userId}:${data.campaignId}:${data.shouldPrompt}:${data.decidedBy}`;
  }

  // For survey chain detection, key includes: name, userId, oldCampaignId, success
  if (telemetryName === 'surveyChainDetection') {
    return `${telemetryName}:${userId}:${data.oldCampaignId}:${data.success}`;
  }

  // Generic: Use telemetry name and userId
  return `${telemetryName}:${userId}`;
}

/**
 * Check if telemetry event should be logged (debouncing)
 * Returns true if event hasn't been logged recently
 */
function shouldLogTelemetry(telemetryName: string, userId: string, data: any): boolean {
  const key = getTelemetryKey(telemetryName, userId, data);
  const now = Date.now();
  const lastLogged = recentTelemetryEvents.get(key);

  if (lastLogged && now - lastLogged < TELEMETRY_DEBOUNCE_MS) {
    console.debug(`[Analytics] Debouncing telemetry event: ${key} (logged ${now - lastLogged}ms ago)`);
    return false;
  }

  // Record this event
  recentTelemetryEvents.set(key, now);

  // Clean up old entries (older than debounce window)
  for (const [k, timestamp] of recentTelemetryEvents.entries()) {
    if (now - timestamp > TELEMETRY_DEBOUNCE_MS) {
      recentTelemetryEvents.delete(k);
    }
  }

  return true;
}

// Combines a unit name with a story title into the Mixpanel `story`
// breakdown field's goal format (PE-1614).
export function buildStoryName(unit: string, story: string): string {
  return `${unit} - ${story}`;
}

function extractCommonUserProperties(userProfile: UserProfileType) {
  const { type, userId, schoolId, district, account, seller, licenseLevel, licenseExpires } = userProfile;

  return {
    userType: type as UserType,
    userId,
    schoolId: schoolId || 'none',
    district: district || 'none',
    account: account || 'none',
    seller: seller || 'none',
    licenseLevel,
    licenseExpires,
    commitHash:
      import.meta.env.REACT_MONOREPO_COMMIT_HASH || (import.meta.env.MODE === 'development' ? 'dev-mode' : 'unknown'),
  };
}

export default Analytics;
